import { IIMKashipurMark, IgnouMark, MakautMark, UniversityOfCalcuttaMark } from "../assets";
import { education } from "../content";
import type { EducationEntry } from "../types/content";

/**
 * ============================================================================
 * Education id → institution mark. Presentation, keyed by content-layer id.
 * ============================================================================
 *
 * WHY THIS IS NOT A FIELD IN `education.json`. The layer holds facts about the
 * education; which picture represents an institution is a presentation asset,
 * and the layer has no field for one (the schema has never carried an image for
 * an academic entry). `workshopImages.ts` and `nonCredlyCerts.ts` are the two
 * precedents on this site and both key a display asset off the layer's id
 * rather than putting a bundler import inside a JSON file.
 *
 * THE MAP IS ASSERTED AT MODULE LOAD, IN BOTH DIRECTIONS. D-066 records a gate
 * that was written to check exactly this — that every image-map key names a
 * real record — and that parsed the module's JSDoc example instead of its
 * declaration, so it reported one key against an empty map and passed its own
 * orphan check. The check that cannot be fooled that way is the one the module
 * runs on ITSELF, at load, against the real imports:
 *
 *   * every key names an entry the layer actually carries — a typo, or an id
 *     retired from `education.json`, throws instead of silently rendering the
 *     initials fallback and looking like a design choice;
 *   * every entry the layer carries has a mark — so ADDING a degree without a
 *     crest fails the build rather than shipping one bare node among four
 *     plated ones, which is the state that reads as a bug.
 *
 * The second direction is the one that would otherwise be left out, and it is
 * the one that catches the next edit rather than this one. `prerender.mjs`
 * executes the component tree, so either throw fails the BUILD.
 *
 * IT IS DELIBERATELY NOT OPTIONAL. `Experience_v2` treats a logo as optional
 * because no company logo exists on this site yet and initials are its real
 * fallback. Here all four exist, so "absent" is not a state this section has —
 * and a map that tolerates absence cannot tell a missing crest from a new one.
 */
const MARKS: Readonly<Record<string, string>> = {
	"iim-kashipur-epgdm": IIMKashipurMark,
	"makaut-mca": MakautMark,
	"ignou-pgdca": IgnouMark,
	"cu-bsc": UniversityOfCalcuttaMark
};

const layerIds: string[] = education.map((entry: EducationEntry) => entry.id);
const mapKeys: string[] = Object.keys(MARKS);

const orphans: string[] = mapKeys.filter((id: string) => !layerIds.includes(id));
if (orphans.length) {
	throw new Error(
		`educationLogos: ${orphans.join(", ")} name no entry in education.json. A mark keyed to an id the layer ` +
			"does not carry renders nowhere, and the section would fall back to initials as though that were the design."
	);
}

const unmarked: string[] = layerIds.filter((id: string) => !mapKeys.includes(id));
if (unmarked.length) {
	throw new Error(
		`educationLogos: ${unmarked.join(", ")} have no institution mark. Every education entry carries one, so a ` +
			"new degree must arrive with its crest — one plain node beside three plated ones reads as a defect."
	);
}

/** The institution's mark for a content-layer education id. Throws above if one is missing. */
export const educationMark = (id: string): string => MARKS[id];
